/* eslint-disable */
import React, { useContext, useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { AlertAdapter } from 'pg-ui-library';
import { useLocalStorage } from 'src/hooks/useLocalStorage';
import { useActions } from 'src/hooks/useActions';
import { useTypedSelector } from 'src/hooks/useTypedSelector';
import { useTranslation } from 'react-i18next';
import signinService from 'src/services/signin.service';
import { MFAForm } from 'src/modules/login/components/mfa-form';
import { AuthContext, IAuthContext, StoreContextProvider } from 'src/modules/login/auth.context';

import loginImg from 'src/assets/login.png';
import logoSvg from 'src/assets/logo_pride.svg';

import {
  Container,
  FormContainer,
  ImageContainer,
  FooterText,
  LinkContainer,
  FooterContainer,
  MenuContainer,
  BackgroundImage,
  Logo,
  FooterLink,
  FooterRouteLink,
  LogoContainer,
} from 'src/modules/login/styles';

interface MFARouteState {
  username: string;
  password: string;
  preference: 'authenticator' | 'email' | null;
  mfaActivated: boolean;
}

const resolveInitialPreference = (state: MFARouteState | null): 'mfa' | 'email' | null => {
  if (!state) return null;
  if (state.preference === 'email') return 'email';
  if (state.preference === 'authenticator' || state.mfaActivated) return 'mfa';
  return null;
};

const MFAPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const routeState = location.state as MFARouteState | null;
  const { t } = useTranslation();
  const currentYear = new Date().getFullYear();

  const { data } = useTypedSelector(s => s.auth);
  const redirectPath = data?.redirectPath;
  const userRole = data?.userRole;

  const [token, setToken] = useLocalStorage('accessToken', null);
  const [modules, setModules] = useLocalStorage('modules', []);
  const [userRoleSetting, setUserRoleSetting] = useLocalStorage('userRole', null);

  const { setUser, setPass } = useContext(AuthContext) as IAuthContext;

  const initialPreference = resolveInitialPreference(routeState);

  const [expandedMethod, setExpandedMethod] = useState<'mfa' | 'email' | null>(
    initialPreference === 'mfa' ? 'mfa' : null
  );
  const [loadingMethod, setLoadingMethod] = useState<'mfa' | 'email' | null>(null);
  const [determinedPreference] = useState<'mfa' | 'email' | null>(initialPreference);

  const methodRadioGroupName = 'mfa-page-verification-method';

  // Guard: redirect if no credentials were passed (e.g. direct URL access)
  useEffect(() => {
    if (!routeState?.username || !routeState?.password) {
      navigate('/', { replace: true });
      return;
    }

    setUser(routeState.username);
    setPass(routeState.password);

    if (initialPreference === 'email') {
      const sendEmailCode = async () => {
        setLoadingMethod('email');
        try {
          const response = await signinService.requestEmailCode({
            username: routeState.username,
            password: routeState.password,
          });
          const destinationEmail = response?.data?.destinationEmail || routeState.username;
          AlertAdapter(`A confirmation code was sent to ${destinationEmail}`, 'success');
          setExpandedMethod('email');
        } catch (e: any) {
          AlertAdapter(
            e?.response?.status === 429
              ? 'Account temporarily locked due to too many failed MFA attempts. Please try again in 1 hour.'
              : 'Unable to send confirmation code by email',
            'error'
          );
        } finally {
          setLoadingMethod(null);
        }
      };
      sendEmailCode();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Persist token from Redux after successful auth
  useEffect(() => {
    if (data?.accessToken) {
      setToken(data.accessToken);
      setUserRoleSetting(data?.userRole);
      setModules(data.modules);
    }
  }, [data?.accessToken]); // eslint-disable-line react-hooks/exhaustive-deps

  // Navigate to dashboard after token is stored
  useEffect(() => {
    if (token && modules.length) {
      const target = redirectPath || '/dashboard';
      navigate(target, { replace: true });
    }
  }, [token, modules, redirectPath, userRole]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAuthMethodSelection = async (method: 'mfa' | 'email') => {
    if (!routeState || expandedMethod === method || loadingMethod !== null) return;

    if (method === 'email') {
      try {
        setLoadingMethod('email');
        const response = await signinService.requestEmailCode({
          username: routeState.username,
          password: routeState.password,
        });
        const destinationEmail = response?.data?.destinationEmail || routeState.username;
        AlertAdapter(`A confirmation code was sent to ${destinationEmail}`, 'success');
      } catch (e: any) {
        AlertAdapter(
          e?.response?.status === 429
            ? 'Account temporarily locked due to too many failed MFA attempts. Please try again in 1 hour.'
            : t('login.form.error'),
          'error'
        );
        return;
      } finally {
        setLoadingMethod(null);
      }
    }

    setExpandedMethod(method);
  };

  const isInitialLoading = loadingMethod !== null && expandedMethod === null;

  return (
    <Container>
      <style>{`@keyframes pg-spin { to { transform: rotate(360deg); } }`}</style>
      <MenuContainer>
        <LogoContainer>
          <Logo src={logoSvg} alt="Pride Global logo" />
        </LogoContainer>

        <FormContainer>
          <div style={{ width: '400px', maxWidth: '92vw', display: 'flex', flexDirection: 'column', gap: '14px' }}>
            <h3 style={{ margin: 0, fontSize: '24px', color: '#1f2937' }}>Secure sign in</h3>
            <p style={{ margin: 0, color: '#6b7280', fontSize: '14px' }}>
              {isInitialLoading
                ? 'Loading your sign-in method...'
                : determinedPreference
                ? 'Verify your identity using your preferred method.'
                : 'Select one of the secure methods below to verify your login.'}
            </p>

            {/* Spinner while email code is being requested on mount */}
            {isInitialLoading && (
              <div style={{ display: 'flex', justifyContent: 'center', padding: '24px 0' }}>
                <span
                  style={{
                    width: '32px',
                    height: '32px',
                    border: '3px solid #c7cef0',
                    borderTopColor: '#4948ac',
                    borderRadius: '50%',
                    display: 'inline-block',
                    animation: 'pg-spin 0.6s linear infinite',
                  }}
                />
              </div>
            )}

            {/* No preference yet: pick one method (only until user chooses) */}
            {!isInitialLoading && expandedMethod === null && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <label
                  htmlFor="mfa-page-pick-mfa"
                  style={{
                    width: '100%',
                    borderRadius: '14px',
                    border: '1px solid #d9def0',
                    background: 'linear-gradient(180deg, #f7f8ff 0%, #eef1ff 100%)',
                    padding: '14px 16px',
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'flex-start',
                    gap: '12px',
                    cursor: loadingMethod !== null ? 'default' : 'pointer',
                    margin: 0,
                  }}
                >
                  <input
                    id="mfa-page-pick-mfa"
                    type="radio"
                    name={methodRadioGroupName}
                    value="mfa"
                    checked={expandedMethod === 'mfa'}
                    disabled={loadingMethod !== null}
                    onChange={() => handleAuthMethodSelection('mfa')}
                    style={{
                      accentColor: '#4948ac',
                      width: '18px',
                      height: '18px',
                      flexShrink: 0,
                      marginTop: '2px',
                      cursor: loadingMethod !== null ? 'default' : 'pointer',
                    }}
                  />
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '4px' }}>
                    <span style={{ fontSize: '15px', fontWeight: 700, color: '#2f3b8f' }}>Authenticator App</span>
                    <span style={{ fontSize: '12px', color: '#556080' }}>
                      Use your app to generate a one-time verification code.
                    </span>
                  </div>
                </label>
                <label
                  htmlFor="mfa-page-pick-email"
                  style={{
                    width: '100%',
                    borderRadius: '14px',
                    border: '1px solid #d9def0',
                    background: 'linear-gradient(180deg, #ffffff 0%, #f5f7ff 100%)',
                    padding: '14px 16px',
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'flex-start',
                    gap: '12px',
                    cursor: loadingMethod !== null ? 'default' : 'pointer',
                    margin: 0,
                  }}
                >
                  <input
                    id="mfa-page-pick-email"
                    type="radio"
                    name={methodRadioGroupName}
                    value="email"
                    checked={expandedMethod === 'email'}
                    disabled={loadingMethod !== null}
                    onChange={() => handleAuthMethodSelection('email')}
                    style={{
                      accentColor: '#4948ac',
                      width: '18px',
                      height: '18px',
                      flexShrink: 0,
                      marginTop: '2px',
                      cursor: loadingMethod !== null ? 'default' : 'pointer',
                    }}
                  />
                  <div
                    style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '4px', flex: 1 }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                      <span style={{ fontSize: '15px', fontWeight: 700, color: '#2f3b8f' }}>
                        Email Confirmation Code
                      </span>
                      {loadingMethod === 'email' && (
                        <span
                          style={{
                            width: '14px',
                            height: '14px',
                            flexShrink: 0,
                            border: '2px solid #c7cef0',
                            borderTopColor: '#4948ac',
                            borderRadius: '50%',
                            display: 'inline-block',
                            animation: 'pg-spin 0.6s linear infinite',
                          }}
                        />
                      )}
                    </div>
                    <span style={{ fontSize: '12px', color: '#556080' }}>
                      Receive a unique code in your email and enter it here.
                    </span>
                  </div>
                </label>
              </div>
            )}

            {/* Authenticator App only (hidden while email flow is active) */}
            {!isInitialLoading && expandedMethod === 'mfa' && (
              <div>
                <label
                  htmlFor="mfa-page-header-mfa"
                  style={{
                    width: '100%',
                    borderRadius: '14px 14px 0 0',
                    border: '2px solid #4948ac',
                    borderBottom: 'none',
                    background: 'linear-gradient(180deg, #eef1ff 0%, #dce2ff 100%)',
                    padding: '14px 16px',
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'flex-start',
                    gap: '12px',
                    cursor: loadingMethod !== null ? 'default' : 'pointer',
                    transition: 'all 0.15s ease',
                    margin: 0,
                  }}
                >
                  <input
                    id="mfa-page-header-mfa"
                    type="radio"
                    name={methodRadioGroupName}
                    value="mfa"
                    checked
                    onChange={() => {}}
                    disabled={loadingMethod !== null}
                    style={{
                      accentColor: '#4948ac',
                      width: '18px',
                      height: '18px',
                      flexShrink: 0,
                      marginTop: '2px',
                      cursor: loadingMethod !== null ? 'default' : 'pointer',
                    }}
                  />
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '4px' }}>
                    <span style={{ fontSize: '15px', fontWeight: 700, color: '#2f3b8f' }}>Authenticator App</span>
                    <span style={{ fontSize: '12px', color: '#556080' }}>
                      Use your app to generate a one-time verification code.
                    </span>
                  </div>
                </label>
                <div
                  style={{
                    border: '2px solid #4948ac',
                    borderTop: 'none',
                    borderRadius: '0 0 14px 14px',
                    padding: '16px',
                    background: '#fafbff',
                  }}
                >
                  <MFAForm
                    mode="mfa"
                    methodSwitcher={{
                      radioGroupName: methodRadioGroupName,
                      expandedMethod,
                      onSelectMethod: handleAuthMethodSelection,
                      disabled: loadingMethod !== null,
                    }}
                  />
                </div>
              </div>
            )}

            {/* Email confirmation only (hidden while authenticator flow is active) */}
            {!isInitialLoading && expandedMethod === 'email' && (
              <div>
                <label
                  htmlFor="mfa-page-header-email"
                  style={{
                    width: '100%',
                    borderRadius: '14px 14px 0 0',
                    border: '2px solid #4948ac',
                    borderBottom: 'none',
                    background: 'linear-gradient(180deg, #eef1ff 0%, #dce2ff 100%)',
                    padding: '14px 16px',
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'flex-start',
                    gap: '12px',
                    cursor: loadingMethod !== null ? 'default' : 'pointer',
                    transition: 'all 0.15s ease',
                    margin: 0,
                  }}
                >
                  <input
                    id="mfa-page-header-email"
                    type="radio"
                    name={methodRadioGroupName}
                    value="email"
                    checked
                    onChange={() => {}}
                    disabled={loadingMethod !== null}
                    style={{
                      accentColor: '#4948ac',
                      width: '18px',
                      height: '18px',
                      flexShrink: 0,
                      marginTop: '2px',
                      cursor: loadingMethod !== null ? 'default' : 'pointer',
                    }}
                  />
                  <div
                    style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '4px', flex: 1 }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                      <span style={{ fontSize: '15px', fontWeight: 700, color: '#2f3b8f' }}>
                        Email Confirmation Code
                      </span>
                      {loadingMethod === 'email' && (
                        <span
                          style={{
                            width: '14px',
                            height: '14px',
                            flexShrink: 0,
                            border: '2px solid #c7cef0',
                            borderTopColor: '#4948ac',
                            borderRadius: '50%',
                            display: 'inline-block',
                            animation: 'pg-spin 0.6s linear infinite',
                          }}
                        />
                      )}
                    </div>
                    <span style={{ fontSize: '12px', color: '#556080' }}>
                      Receive a unique code in your email and enter it here.
                    </span>
                  </div>
                </label>
                <div
                  style={{
                    border: '2px solid #4948ac',
                    borderTop: 'none',
                    borderRadius: '0 0 14px 14px',
                    padding: '16px',
                    background: '#fafbff',
                  }}
                >
                  <MFAForm
                    mode="email"
                    methodSwitcher={{
                      radioGroupName: methodRadioGroupName,
                      expandedMethod,
                      onSelectMethod: handleAuthMethodSelection,
                      disabled: loadingMethod !== null,
                    }}
                  />
                </div>
              </div>
            )}
          </div>
        </FormContainer>

        <FooterContainer>
          <FooterText>{t('signUp.footer.rights', { year: currentYear })}</FooterText>
          <LinkContainer>
            <FooterRouteLink to="/terms-of-use">Terms of Use</FooterRouteLink>
            <FooterRouteLink to="/privacy-policy">Privacy Policy</FooterRouteLink>
            <FooterRouteLink to="/cookie-policy">Cookie Policy</FooterRouteLink>
            <FooterLink href="mailto:ESSsupport@prideglobal.com" target="_blank">
              {t('login.footer.support')}
            </FooterLink>
            <FooterLink href="User.Guide.pdf" target="_blank">
              {t('login.footer.guide')}
            </FooterLink>
          </LinkContainer>
        </FooterContainer>
      </MenuContainer>

      <ImageContainer>
        <BackgroundImage src={loginImg} alt="Background image" />
      </ImageContainer>
    </Container>
  );
};

export default function MFAPageWrapper() {
  return (
    <StoreContextProvider>
      <MFAPage />
    </StoreContextProvider>
  );
}
